import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel } from "@/components/admin/ui";
import { INDUSTRIES } from "@/data/industries";
import { timezoneOptionsWithDefault, detectDefaultTimezone } from "@/lib/timezones";
import { COUNTRIES, STATES_BY_COUNTRY } from "@/lib/geoData";
import { useSaveStatus } from "./useSaveStatus";
import { inputClass, labelClass, saveBtnClass } from "./orgFormStyles";

const emptyAddress = { street: "", line2: "", city: "", state: "", zip: "", country: "US" };

const Field = ({ label, value, onChange, testId, type = "text", required, placeholder }) => (
  <div>
    <label className={labelClass}>{label}{required ? " *" : ""}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className={inputClass} />
  </div>
);

export default function OrgSetupTab({ org, API, onSaved }) {
  const [form, setForm] = useState(() => ({
    legal_name: org.legal_name || "",
    name: org.name || "",
    industry: org.industry || "",
    business_type: org.business_type || "",
    website: org.website || "",
    business_email: org.business_email || "",
    business_phone: org.business_phone || "",
    address: { ...emptyAddress, ...(org.address || {}), country: (org.address || {}).country || "US" },
    timezone: org.timezone || detectDefaultTimezone(),
    logo_url: org.logo_url || "",
    description: org.description || "",
    primary_contact: org.primary_contact || "",
    primary_contact_title: org.primary_contact_title || "",
    primary_contact_email: org.primary_contact_email || "",
    primary_contact_phone: org.primary_contact_phone || "",
  }));
  const { saving, setSaving, error, setError, savedFlash, flash } = useSaveStatus();

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setAddr = (patch) => setForm((f) => ({ ...f, address: { ...f.address, ...patch } }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/admin/organizations/${org.org_id}/profile`, form, { withCredentials: true });
      onSaved(data);
      flash();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="org-setup-tab">
      <Panel title="Business Details">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Legal / Business Name" required value={form.legal_name} onChange={(v) => set({ legal_name: v })} testId="org-setup-legal-name" />
          <Field label="Display Name" required value={form.name} onChange={(v) => set({ name: v })} testId="org-setup-display-name" />
          <div>
            <label className={labelClass}>Industry *</label>
            <select value={form.industry} onChange={(e) => set({ industry: e.target.value })} data-testid="org-setup-industry" className={inputClass}>
              <option value="">Select industry</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <Field label="Business Type" value={form.business_type} onChange={(v) => set({ business_type: v })} testId="org-setup-business-type" />
          <Field label="Website" value={form.website} onChange={(v) => set({ website: v })} testId="org-setup-website" />
          <Field label="Business Email" required type="email" value={form.business_email} onChange={(v) => set({ business_email: v })} testId="org-setup-business-email" />
          <Field label="Business Phone" required value={form.business_phone} onChange={(v) => set({ business_phone: v })} testId="org-setup-business-phone" />
          <Field label="Organization Logo (URL)" value={form.logo_url} onChange={(v) => set({ logo_url: v })} testId="org-setup-logo-url" placeholder="https://…" />
        </div>
        <div className="mt-4">
          <label className={labelClass}>Short Business Description</label>
          <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="org-setup-description" className={`${inputClass} min-h-20`} />
        </div>
      </Panel>

      <Panel title="Business Address">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Street Address" required value={form.address.street} onChange={(v) => setAddr({ street: v })} testId="org-setup-address-street" />
          <Field label="Address Line 2" value={form.address.line2} onChange={(v) => setAddr({ line2: v })} testId="org-setup-address-line2" />
          <Field label="City" required value={form.address.city} onChange={(v) => setAddr({ city: v })} testId="org-setup-address-city" />
          <div>
            <label className={labelClass}>State / Province *</label>
            {STATES_BY_COUNTRY[form.address.country] ? (
              <select value={form.address.state} onChange={(e) => setAddr({ state: e.target.value })} data-testid="org-setup-address-state" className={inputClass}>
                <option value="">Select…</option>
                {STATES_BY_COUNTRY[form.address.country].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input value={form.address.state} onChange={(e) => setAddr({ state: e.target.value })} data-testid="org-setup-address-state" className={inputClass} />
            )}
          </div>
          <Field label="ZIP / Postal Code" required value={form.address.zip} onChange={(v) => setAddr({ zip: v })} testId="org-setup-address-zip" />
          <div>
            <label className={labelClass}>Country *</label>
            <select value={form.address.country} onChange={(e) => setAddr({ country: e.target.value, state: "" })} data-testid="org-setup-address-country" className={inputClass}>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Time Zone *</label>
            <select value={form.timezone} onChange={(e) => set({ timezone: e.target.value })} data-testid="org-setup-timezone" className={inputClass}>
              {timezoneOptionsWithDefault(form.timezone).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
      </Panel>

      <Panel title="Primary Contact">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full Name" required value={form.primary_contact} onChange={(v) => set({ primary_contact: v })} testId="org-setup-contact-name" />
          <Field label="Job Title" value={form.primary_contact_title} onChange={(v) => set({ primary_contact_title: v })} testId="org-setup-contact-title" />
          <Field label="Email" required type="email" value={form.primary_contact_email} onChange={(v) => set({ primary_contact_email: v })} testId="org-setup-contact-email" />
          <Field label="Phone" required value={form.primary_contact_phone} onChange={(v) => set({ primary_contact_phone: v })} testId="org-setup-contact-phone" />
        </div>
      </Panel>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-setup-error">{error}</p>}
      <div className="flex items-center gap-4">
        <button type="button" onClick={save} disabled={saving} data-testid="org-setup-save-btn" className={saveBtnClass}>
          {saving ? "Saving…" : "Save Org Setup"}
        </button>
        {savedFlash && <span className="text-sm font-semibold text-emerald-600" data-testid="org-setup-saved-flash">Saved</span>}
      </div>
    </div>
  );
}
